/**
 * Generic Admin Detail Page
 * Reusable detail page component that wraps useAdminDetail with consistent UI
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { RefreshCw, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { AdminPageShell, AdminCardShell, AdminSection } from './AdminPageShell';
import { useAdminDetail, useAdminMutation } from '../hooks';

/**
 * Generic Admin Detail Page
 * 
 * @param {Object} props
 * @param {string} props.resource - Resource name (e.g., 'users', 'series')
 * @param {string} props.idParam - URL param name for ID (default: 'id')
 * @param {string} props.title - Page title (can be function receiving data)
 * @param {string} props.subtitle - Page subtitle (can be function receiving data)
 * @param {React.ElementType} props.icon - Icon component
 * @param {string} props.headerColor - Header gradient color
 * @param {string} props.backLink - Back link path
 * @param {string} props.backLabel - Back link label
 * @param {React.ReactNode|Function} props.children - Content (function receives data, actions)
 * @param {Array} props.headerActions - Header action buttons
 */
export const AdminDetailPage = ({
  resource,
  idParam = 'id',
  title,
  subtitle,
  icon: Icon,
  headerColor = 'from-purple-600 to-indigo-600',
  backLink,
  backLabel,
  children,
  headerActions: customHeaderActions,
}) => {
  const params = useParams();
  const id = params[idParam];

  const {
    data,
    loading,
    error,
    isAuthError,
    refresh,
    updateLocal,
    hasData,
    notFound,
  } = useAdminDetail(resource, id);

  const mutation = useAdminMutation(resource);

  // Resolve title/subtitle if they are functions
  const resolvedTitle = typeof title === 'function' ? title(data) : title;
  const resolvedSubtitle = typeof subtitle === 'function' ? subtitle(data) : subtitle;

  // Build header actions
  const headerActions = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={refresh}
        className="border-white/30 text-white hover:bg-white/10"
      >
        <RefreshCw className="w-4 h-4 mr-2" />
        Refresh
      </Button>
      {customHeaderActions}
    </>
  );

  // Render children with data and helpers
  const renderContent = () => {
    if (typeof children === 'function') {
      return children({
        data,
        loading,
        error,
        refresh,
        updateLocal,
        mutation,
        // Helper to update data on server and locally
        async updateData(updates) {
          const result = await mutation.patch(id, updates);
          if (result.ok) {
            updateLocal(updates);
          }
          return result;
        },
      });
    }
    return children;
  };

  return (
    <AdminPageShell
      title={resolvedTitle || `${resource} Detail`}
      subtitle={resolvedSubtitle}
      icon={Icon}
      headerColor={headerColor}
      backLink={backLink || `/admin/${resource}`}
      backLabel={backLabel || `Back to ${resource}`}
      loading={loading}
      error={error}
      isAuthError={isAuthError}
      onRetry={refresh}
      headerActions={headerActions}
    >
      {hasData && renderContent()}
    </AdminPageShell>
  );
};

/**
 * Admin Detail Card - Consistent card for detail page sections
 */
export const AdminDetailCard = ({
  title,
  subtitle,
  icon,
  children,
  actions,
  loading = false,
  error = null,
  className = '',
}) => {
  return (
    <AdminCardShell
      title={title}
      subtitle={subtitle}
      icon={icon}
      actions={actions}
      loading={loading}
      error={error}
      className={className}
    >
      {children}
    </AdminCardShell>
  );
};

/**
 * Admin Detail Field - Display a label/value pair
 */
export const AdminDetailField = ({
  label,
  value,
  icon: Icon,
  copyable = false,
  className = '',
}) => {
  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(String(value));
    }
  };

  return (
    <div className={`space-y-1 ${className}`}>
      <dt className="text-sm text-zinc-400 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />}
        {label}
      </dt>
      <dd className="text-white flex items-center gap-2">
        {value || <span className="text-zinc-500">—</span>}
        {copyable && value && (
          <button
            onClick={handleCopy}
            className="text-zinc-500 hover:text-zinc-300 transition-colors"
            title="Copy to clipboard"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
            </svg>
          </button>
        )}
      </dd>
    </div>
  );
};

/**
 * Admin Detail Grid - Grid layout for detail fields
 */
export const AdminDetailGrid = ({
  children,
  columns = 2,
  className = '',
}) => {
  const colsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <dl className={`grid ${colsClass[columns] || colsClass[2]} gap-6 ${className}`}>
      {children}
    </dl>
  );
};

export default AdminDetailPage;
